import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

interface TableState {
  page?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  callRecordStartDate?: string;
  callRecordEndDate?: string;
  clientIds?: number[];
  serviceIds?: number[];
  sortBy?: string;
  orderBy?: string;
  selectedIds?: number[];
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class StateManagementService {
  private tableState = new BehaviorSubject<TableState>({});
  currentState$ = this.tableState.asObservable();
  private lastValidState: TableState = {};
  private lastPageVisited: string | null = null;
  private isUpdatingState = false;
  private isRestoringState = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {
    // Initialize state from URL on service creation
    this.route.queryParams.subscribe(params => {
      // Skip if we're already updating state
      if (this.isUpdatingState) return;

      const state: TableState = {};
      if (params['page']) state.page = parseInt(params['page']);
      if (params['search']) state.search = params['search'];
      if (params['startDate']) state.startDate = params['startDate'];
      if (params['endDate']) state.endDate = params['endDate'];
      if (params['callRecordStartDate']) state.callRecordStartDate = params['callRecordStartDate'];
      if (params['callRecordEndDate']) state.callRecordEndDate = params['callRecordEndDate'];
      if (params['clientIds']) state.clientIds = params['clientIds'].split(',').map(Number);
      if (params['serviceIds']) state.serviceIds = params['serviceIds'].split(',').map(Number);
      if (params['sortBy']) state.sortBy = params['sortBy'];
      if (params['orderBy']) state.orderBy = params['orderBy'];
      if (params['selectedIds']) state.selectedIds = params['selectedIds'].split(',').map(Number);

      // Only update if there are actual changes
      if (Object.keys(state).length > 0) {
        this.lastValidState = { ...state };
        this.tableState.next({ ...state });
      }
    });

    // Listen to navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Get the current URL path segments
      const urlSegments = event.url.split('?')[0].split('/');
      const currentPage = urlSegments[urlSegments.length - 1];

      // Check if we're navigating between list and detail views
      const isDetailView = event.url.includes('/request/');

      // Define all request list views patterns
      const isListView = currentPage.includes('-requests') ||
        currentPage === 'requests-assigned-to-me' ||
        event.url.includes('/requests-assigned-to-me');

      // Check specifically for the assigned-to-me page
      const isAssignedToMePage = currentPage === 'requests-assigned-to-me' ||
        event.url.includes('/requests-assigned-to-me');

      // Check if previous page was a list view
      const wasOnListView = this.lastPageVisited?.includes('-requests') ||
        this.lastPageVisited === 'requests-assigned-to-me' ||
        (this.lastPageVisited && this.lastPageVisited.includes('requests-assigned-to-me'));

      if (currentPage !== this.lastPageVisited) {
        // Navigate between different list views - need to clear state
        if (isListView && wasOnListView) {
          // Only clear if not going between detail and its parent list
          const lastPageBase = this.lastPageVisited?.split('-')[0];
          const currentPageBase = currentPage.split('-')[0];

          // If we're moving between different types of request lists, clear the state
          if ((isAssignedToMePage && !this.lastPageVisited?.includes('assigned-to-me')) ||
            (!isAssignedToMePage && this.lastPageVisited?.includes('assigned-to-me')) ||
            (lastPageBase !== currentPageBase && !isAssignedToMePage)) {
            this.clearState();
          }
        }
        // Navigate away from requests altogether - clear state
        else if (!isDetailView && !isListView && wasOnListView) {
          this.clearState();
        }
        // Return to list view from elsewhere (not from detail view) - restore state
        else if (isListView && !isDetailView) {
          this.restoreState();
        }
      }

      // Store the current page for next comparison
      this.lastPageVisited = currentPage;
    });
  }

  updateState(newState: Partial<TableState>) {
    // Skip if we're restoring state
    if (this.isRestoringState) return;
    // Set flag to prevent circular updates
    this.isUpdatingState = true;
  
    const currentState = this.tableState.value;
    let updatedState = { ...currentState, ...newState };
  
    // Reset pagination when search or date filters are applied
    if (
      (newState.search !== undefined && newState.search !== currentState.search) ||
      (newState.startDate !== undefined && newState.startDate !== currentState.startDate) ||
      (newState.endDate !== undefined && newState.endDate !== currentState.endDate) ||
      (newState.callRecordStartDate !== undefined && newState.callRecordStartDate !== currentState.callRecordStartDate) ||
      (newState.callRecordEndDate !== undefined && newState.callRecordEndDate !== currentState.callRecordEndDate) ||
      (newState.clientIds !== undefined && JSON.stringify(newState.clientIds) !== JSON.stringify(currentState.clientIds)) ||
      (newState.serviceIds !== undefined && JSON.stringify(newState.serviceIds) !== JSON.stringify(currentState.serviceIds))
    ) {
      // Always explicitly set page to 1 when filters change
      updatedState = { ...updatedState, page: 1 };
    }
    
    // Only update if there are actual changes to avoid infinite loops
    if (JSON.stringify(this.lastValidState) !== JSON.stringify(updatedState)) {
      this.lastValidState = updatedState;
      this.tableState.next(updatedState);
      this.updateUrl(updatedState);
    }
  
    // Reset flag after a short delay to allow other operations to complete
    setTimeout(() => {
      this.isUpdatingState = false;
    }, 100);
  }

  getQueryParams(): TableState {
    return this.lastValidState;
  }

  private updateUrl(state: TableState) {
    // Remove undefined, null, or empty values
    const cleanState = Object.entries(state)
      .filter(([_, value]) => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (Array.isArray(value) && value.length === 1 && value[0] === 0) return false;
        return true;
      })
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Update URL without navigation
    this.location.replaceState(
      this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: cleanState,
        queryParamsHandling: 'merge'
      }).toString()
    );
  }

  private restoreState() {
    if (Object.keys(this.lastValidState).length > 0) {
      // Set flags to prevent circular updates
      this.isUpdatingState = true;
      this.isRestoringState = true;

      // Create a deep copy of the state to avoid reference issues
      const stateToRestore = JSON.parse(JSON.stringify(this.lastValidState));

      // First update URL
      this.updateUrl(stateToRestore);

      // Then emit state with a slight delay to ensure URL is updated first
      setTimeout(() => {
        // Emit the state with a fresh object to ensure change detection
        this.tableState.next({ ...stateToRestore });

        // Add an additional emit to ensure all components receive the state
        // This is critical for pagination UI updates
        setTimeout(() => {
          this.tableState.next({ ...stateToRestore });

          // Reset flags after state is restored
          this.isUpdatingState = false;
          this.isRestoringState = false;
        }, 100);
      }, 50);
    }
  }

  clearState() {
    // Set flag to prevent circular updates
    this.isUpdatingState = true;

    this.lastValidState = {};
    this.tableState.next({});
    // this.updateUrl({});

    // Clear URL parameters
    this.location.replaceState(
      this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: {}
      }).toString()
    );

    // Reset flag after a short delay
    setTimeout(() => {
      this.isUpdatingState = false;
    }, 100);
  }
}